//#region IMPORT
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  Box,
  Card,
  Typography,
  IconButton,
  Tooltip,
  styled,
  tooltipClasses,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Icon from '../../../../Assets/Icon/CustomIcons';
import FileUploadDialog from '../../../../Components/FileUploadDialog/FileUploadDialog';
import HAM_BASIC_02DetailCellRenderer from './HAM_BASIC_02DetailCellRenderer';
import {
  getColumnDefs1,
} from './HAM_BASIC_02_ColumnDefs';
//#endregion

const LargeTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontSize: '0.9rem',
    padding: theme.spacing(1),
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#4CAF50',
  },
}));
function HAM_BASIC_02(props) {
  const title = 'MSDS 관리대장';
  const [gridApi, setGridApi] = useState(null);
  const detailGridsRef = useRef(new Map()); // 모든 상세 그리드 API를 저장
  const suppressMasterSelectionRef = useRef(false); // 상세 → 마스터 동기화 시 이벤트 방지
  const masterSelectionModeRef = useRef(new Map()); // 마스터가 디테일 선택을 주도했는지 추적
  const masterNoCounterRef = useRef(0);

  const getNextMasterNo = useCallback(() => {
    masterNoCounterRef.current += 1;
    return masterNoCounterRef.current;
  }, []);

  useEffect(() => {
    const numericNos = (props.rowData1 || [])
      .map((row) => Number(row.no))
      .filter((value) => Number.isFinite(value));
    const maxNo = numericNos.length ? Math.max(...numericNos) : 0;
    masterNoCounterRef.current = maxNo;
  }, [props.rowData1]);

  const columnDefs = useMemo(() => {
    const baseColumnDefs = getColumnDefs1(props);
    // 제품명 컬럼에 agGroupCellRenderer 추가 (Master-Detail 구조용)
    const productNameColumn = baseColumnDefs.find((col) => col.field === 'productName');
    if (productNameColumn) {
      productNameColumn.cellRenderer = 'agGroupCellRenderer';
      productNameColumn.cellRendererParams = {
        suppressCount: true,
        innerRenderer: (params) => {
          return params.value || '';
        },
      };
    }
    return baseColumnDefs;
  }, [props]);

  // 상세 그리드 등록/해제 함수
  const RegisterDetailGrid = useCallback((rowId, detailApi) => {
    if (rowId && detailApi) {
      detailGridsRef.current.set(rowId, detailApi);
    }
  }, []);

  const UnregisterDetailGrid = useCallback((rowId) => {
    if (rowId) {
      detailGridsRef.current.delete(rowId);
    }
  }, []);

  // 모든 상세 그리드에서 선택된 행 수집
  const GetAllSelectedDetailRows = useCallback(() => {
    const selectedRows = [];
    detailGridsRef.current.forEach((detailApi) => {
      const detailSelected = detailApi?.getSelectedRows() || [];
      selectedRows.push(...detailSelected);
    });
    return selectedRows;
  }, []);

  // DTL 그리드 삭제 예정 처리 (각 그리드별로 독립적으로 처리)
  const HandleMarkedForDeletion2 = useCallback(
    (rows) => {
      if (!rows || rows.length === 0) {
        props.dispatch?.(
          props.showSnackbar?.({
            message: '삭제할 행을 선택하세요.',
            type: 'warning',
          })
        );
        return;
      }

      // hbmId별로 그룹화
      const rowsByHbmId = new Map();
      rows.forEach((row) => {
        const hbmId = row.hbmId;
        if (!rowsByHbmId.has(hbmId)) {
          rowsByHbmId.set(hbmId, []);
        }
        rowsByHbmId.get(hbmId).push(row);
      });

      // 각 hbmId별로 해당하는 마스터의 DTL 그리드 API 찾기
      const masterApi = gridApi || props.gridRef1?.current?.api;
      if (!masterApi) return;

      // 모든 그룹의 updatedRows를 수집
      const allUpdatedRows = [];
      const deleted = props.deletedRowData2 || [];

      rowsByHbmId.forEach((groupRows, hbmId) => {
        // hbmId로 마스터 행 찾기
        let masterNo = null;
        masterApi.forEachNode((node) => {
          if (node.data?.hbmId === hbmId) {
            masterNo = node.data?.no;
          }
        });

        if (!masterNo) return;

        // 해당 마스터의 DTL 그리드 API 가져오기
        const detailApi = detailGridsRef.current.get(masterNo);
        if (!detailApi) return;

        // 해당 그리드의 삭제 예정 행만 필터링
        const updatedRows = groupRows.map((row) => {
          const rowNode = detailApi.getRowNode(row.no);
          const originalIndex = rowNode?.rowIndex ?? 0;

          return {
            ...row,
            deleteYn: 'Y',
            originalIndex,
          };
        });

        // 모든 그룹의 updatedRows 수집
        allUpdatedRows.push(...updatedRows);

        // 해당 그리드에만 삭제 예정 행 추가
        updatedRows.forEach((row) => {
          detailApi.applyTransaction({ remove: [row] });
          detailApi.applyTransaction({ add: [row], addIndex: 0 });
        });
      });

      // 모든 그룹 처리 후 한 번에 deletedRowData2 업데이트
      if (allUpdatedRows.length > 0) {
        const updatedDeleted = [
          ...deleted,
          ...allUpdatedRows.filter((r) => !deleted.some((d) => d.no === r.no)),
        ];
        props.setDeletedRowData2?.(updatedDeleted);

        // changedRowData2 업데이트
        if (typeof props.setChangedRowData2 === 'function') {
          props.setChangedRowData2((prev) => {
            const safePrev = Array.isArray(prev) ? prev : [];
            return [
              ...safePrev.filter(
                (p) => !allUpdatedRows.some((r) => r.no === p.no)
              ),
              ...allUpdatedRows.map((r) => ({
                ...r,
                deleteYn: 'Y',
              })),
            ];
          });
        }
      }
    },
    [
      gridApi,
      props.gridRef1,
      props.deletedRowData2,
      props.setDeletedRowData2,
      props.setChangedRowData2,
      props.dispatch,
      props.showSnackbar,
    ]
  );

  // 상세 선택 상태를 마스터 행에 반영한다.
  const SyncDetailSelectionToMaster = useCallback(
    ({ masterNo, allSelected }) => {
      if (!gridApi || !masterNo) return;

      const masterInitiated = masterSelectionModeRef.current.get(masterNo);
      if (!masterInitiated) return; // 마스터가 선택하지 않은 경우에는 동기화하지 않음

      const rowNode = gridApi.getRowNode(masterNo);
      if (!rowNode) return;

      const isSelected = rowNode.isSelected();

      // 마스터가 선택한 이후 디테일 일부가 해제되면 마스터 체크박스를 해제한다.
      if (!allSelected && isSelected) {
        masterSelectionModeRef.current.set(masterNo, false);
        suppressMasterSelectionRef.current = true;
        rowNode.setSelected(false);
      }
    },
    [gridApi]
  );

  // detailCellRenderer 컴포넌트
  const detailCellRenderer = useCallback(
    (params) => {
      return (
        <HAM_BASIC_02DetailCellRenderer
          data={params.data}
          rowData2={props.rowData2}
          originalRowData2={props.originalRowData2}
          setRowData1={props.setRowData1}
          setRowData2={props.setRowData2}
          setSelectedRowData2={props.setSelectedRowData2}
          setChangedRowData2={props.setChangedRowData2}
          setDeletedRowData2={props.setDeletedRowData2}
          setChangedRowData1={props.setChangedRowData1}
          registerDetailGrid={RegisterDetailGrid}
          unregisterDetailGrid={UnregisterDetailGrid}
          syncDetailSelectionToMaster={SyncDetailSelectionToMaster}
          gridRef2={props.gridRef2}
        />
      );
    },
    [
      props.rowData2,
      props.originalRowData2,
      props.setRowData1,
      props.setRowData2,
      props.setSelectedRowData2,
      props.setChangedRowData2,
      props.setDeletedRowData2,
      props.setChangedRowData1,
      RegisterDetailGrid,
      UnregisterDetailGrid,
      props.gridRef2,
      SyncDetailSelectionToMaster,
    ]
  );

  const getRowId = useCallback((params) => {
    return params.data.no;
  }, []);

  // 행 상태에 따른 클래스 추가를 위한 rowClassRules
  const rowClassRules = useMemo(
    () => ({
      'new-row-no-expand': (params) => {
        return params.data?.newYn === 'Y' || !params.data?.hbmId;
      },
      'new-row': (params) => {
        return params.data?.newYn === 'Y';
      },
      'edit-row': (params) => {
        return (
          params.data?.editYn === 'Y' &&
          params.data?.newYn !== 'Y' &&
          params.data?.deleteYn !== 'Y'
        );
      },
      'delete-row': (params) => {
        return params.data?.deleteYn === 'Y';
      },
    }),
    []
  );

  // 신규 행인지 확인하여 확장 가능 여부 결정
  const IsRowExpandable = useCallback((params) => {
    // 신규 행(newYn: 'Y')이거나 hbmId가 없는 경우 확장 불가
    if (params.data?.newYn === 'Y' || !params.data?.hbmId) {
      return false;
    }
    return true;
  }, []);

  // 행 더블클릭 시 신규 행인 경우 확장 방지 및 알림 표시
  const HandleRowDoubleClicked = useCallback(
    (params) => {
      if (!params || !params.node || !params.data) return;

      // 신규 행인 경우 확장 방지 및 알림 표시
      const isNewRow = params.data.newYn === 'Y' || !params.data.hbmId;
      if (isNewRow) {
        // 확장 취소 (혹시 모를 경우를 대비)
        if (params.node.expanded) {
          params.node.setExpanded(false);
        }
        // 알림 표시
        props.dispatch?.(
          props.showSnackbar?.({
            message: '저장 후 이용 가능합니다.',
            type: 'warning',
          })
        );
      }
    },
    [props.dispatch, props.showSnackbar]
  );

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      editable: true,
      filter: true,
      floatingFilter: true,
    }),
    []
  );

  const localeText = useMemo(
    () => ({
      // 기본 텍스트
      page: '페이지',
      more: '더보기',
      to: '~',
      of: '/',
      next: '다음',
      last: '마지막',
      first: '처음',
      previous: '이전',
      loadingOoo: '불러오는 중...',

      // 필터 & 패널 등
      selectAll: '전체 선택',
      searchOoo: '검색...',
      blanks: '공백',

      // 페이지 사이즈 선택기
      pageSizeSelectorLabel: '페이지 당 행 수',

      // 기본 페이지네이션 설명
      paginationPageSize: '페이지 당 행 수',
      paginationNumberFormatter: (params) => `${params.value}`,
    }),
    []
  );

  // 최초 렌더링 시 모든 마스터 행을 접기
  const OnFirstDataRendered = useCallback((params) => {
    setTimeout(() => {
      params.api.forEachNode(function (node) {
        node.setExpanded(false);
      });
    }, 0);
  }, []);

  // 마스터 선택 목록 변화를 상위 상태(selectedRowData1)에 반영한다.
  const HandleMasterSelectionChanged = useCallback(
    (event, setSelectedRowData1, selectedRowNosRef1) => {
      const gridApi = event.api;
      const selectedNodes = gridApi.getSelectedNodes();
      const selectedData = selectedNodes.map((n) => n.data);

      selectedRowNosRef1.current = new Set(
        selectedData.map((r) => r.no)
      );
      setSelectedRowData1(selectedData);
    },
    []
  );

  // 마스터 행 편집 시 변경 내역과 rowData1을 동기화한다.
  const HandleMasterCellValueChanged = useCallback(
    (event, setRowData1, setChangedRowData1) => {
      if (!event?.data || !event?.node) return;

      // 그리드 노드의 현재 데이터에서 deleteYn을 가져와서 보존
      const nodeData = event.node.data;
      const updatedRow = {
        ...event.data,
        editYn: 'Y',
        // 기존 노드 데이터에 deleteYn이 있으면 보존
        ...(nodeData?.deleteYn === 'Y' && { deleteYn: 'Y' }),
      };

      if (typeof setRowData1 === 'function') {
        setRowData1((prev = []) =>
          prev.map((row) => {
            if (row.no === updatedRow.no) {
              // 기존 행의 deleteYn을 보존하면서 업데이트
              return {
                ...updatedRow,
                ...(row.deleteYn === 'Y' && { deleteYn: 'Y' }),
              };
            }
            return row;
          })
        );
      }

      if (typeof setChangedRowData1 === 'function') {
        setChangedRowData1((prev = []) => {
          const filtered = prev.filter((row) => row.no !== updatedRow.no);
          // 기존 changedRowData1에서 deleteYn을 보존
          const existingChangedRow = prev.find(
            (row) => row.no === updatedRow.no
          );
          const finalUpdatedRow = {
            ...updatedRow,
            ...(existingChangedRow?.deleteYn === 'Y' && { deleteYn: 'Y' }),
          };
          return [...filtered, finalUpdatedRow];
        });
      }
    },
    []
  );

  // 마스터 행 선택 시 연결된 상세 그리드의 선택을 동기화한다.
  const HandleMasterRowSelection = useCallback((gridApi, params) => {
    if (!params || !params.node || !params.node.data?.no) return;

    if (suppressMasterSelectionRef.current) {
      suppressMasterSelectionRef.current = false;
      return;
    }

    const masterNo = params.node.data.no;
    const detailApi = detailGridsRef.current.get(masterNo);

    if (!detailApi) return;

    const shouldSelect = params.node.isSelected();
    masterSelectionModeRef.current.set(masterNo, shouldSelect);

    detailApi.forEachNode((node) => {
      node.setSelected(shouldSelect);
    });
  }, []);

  return (
    <Box sx={{ height: '100%' }}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '10px',
          p: 2,
          pt: 1,
          backgroundColor: '#FFF',
          borderColor: 'divider',
        }}
      >
        <Card
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            overflow: 'visible',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              sx={{
                fontSize: '1.05rem',
                fontWeight: 'bold',
                color: '#2CA900',
                mr: 1,
              }}
            >
              {title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LargeTooltip title="추가" arrow placement="top">
              <IconButton
                color="primary"
                sx={{ m: 1, border: 1 }}
                onClick={() => {
                  if (!props.validateSelection?.()) return;
                  if (!props.gridRef1?.current?.api) return;

                  const newRow = {
                    newYn: 'Y',
                    no: getNextMasterNo(),
                  };

                  // 그리드에 행 추가
                  props.gridRef1.current.api.applyTransaction({
                    add: [newRow],
                    addIndex: 0,
                  });

                  // 추가된 행을 rowData1과 changedRowData1에 반영
                  // applyTransaction 후 그리드에서 직접 추가된 행을 가져옴
                  setTimeout(() => {
                    const api = props.gridRef1.current?.api;
                    if (!api) return;

                    // 추가된 행 찾기 (첫 번째 행이 새로 추가된 행)
                    const addedRowNode = api.getDisplayedRowAtIndex(0);
                    if (addedRowNode && addedRowNode.data?.newYn === 'Y') {
                      const addedRow = addedRowNode.data;

                      // rowData1 업데이트
                      if (props.setRowData1) {
                        props.setRowData1((prev = []) => [addedRow, ...prev]);
                      }

                      // changedRowData1 업데이트
                      if (props.setChangedRowData1) {
                        props.setChangedRowData1((prev = []) => {
                          const filtered = prev.filter(
                            (row) => row.no !== addedRow.no
                          );
                          return [addedRow, ...filtered];
                        });
                      }
                    }

                    // 첫 번째 편집 가능한 셀에 포커스
                    const firstEditableColumn = api
                      .getColumnDefs()
                      .find((col) => col.editable && !col.hide);
                    if (firstEditableColumn) {
                      const field = firstEditableColumn.field;
                      api.setFocusedCell(0, field);
                      api.startEditingCell({
                        rowIndex: 0,
                        colKey: field,
                      });
                    }
                  }, 0);
                }}
              >
                <AddIcon />
              </IconButton>
            </LargeTooltip>
            <LargeTooltip title="새로고침" arrow placement="top">
              <IconButton
                color="secondary"
                sx={{ m: 1, border: 1 }}
                onClick={() => {
                  if (!props.validateSelection?.()) return;
                  props.SearchLogic1?.();
                }}
              >
                <RefreshIcon />
              </IconButton>
            </LargeTooltip>
            <LargeTooltip title="삭제" arrow placement="top">
              <IconButton
                color="error"
                sx={{ m: 1, border: 1 }}
                onClick={() => {
                  // 모든 상세 그리드에서 선택된 행 수집
                  const allSelectedDetailRows = GetAllSelectedDetailRows();
                  const hasSelectedMaster = props.selectedRowData1?.length > 0;
                  const hasSelectedDetail = allSelectedDetailRows.length > 0;

                  if (!hasSelectedMaster && !hasSelectedDetail) {
                    props.dispatch?.(
                      props.showSnackbar?.({
                        message: '삭제할 행을 선택해 주세요.',
                        type: 'warning',
                      })
                    );
                    return;
                  }

                  if (hasSelectedMaster) {
                    props.MarkedForDeletion1?.(props.selectedRowData1);
                  }
                  if (hasSelectedDetail) {
                    HandleMarkedForDeletion2(allSelectedDetailRows);
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </LargeTooltip>
            {/* <LargeTooltip title="엑셀 업로드" arrow placement="top">
              <IconButton
                color="secondary"
                sx={{ m: 1, border: 1 }}
                onClick={() => {
                  if (!props.validateSelection?.()) return;
                  props.fileInputRef?.current?.click();
                }}
              >
                <Icon.FolderUpload />
              </IconButton>
            </LargeTooltip>
            <LargeTooltip title="엑셀 다운로드" arrow placement="top">
              <IconButton
                color="primary"
                sx={{ m: 1, border: 1 }}
                onClick={() => {
                  if (!props.validateSelection?.()) return;
                  props.ExcelDownload1?.();
                }}
              >
                <Icon.FolderDownload />
              </IconButton>
            </LargeTooltip> */}
          </Box>
        </Card>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <AgGridReact
            ref={props.gridRef1}
            columnDefs={columnDefs}
            rowData={props.rowData1}
            getRowId={getRowId}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            masterDetail={true}
            detailCellRenderer={detailCellRenderer}
            detailRowHeight={400}
            isRowExpandable={IsRowExpandable}
            rowClassRules={rowClassRules}
            stopEditingWhenCellsLoseFocus={true}
            pagination={true}
            paginationPageSize={100}
            paginationPageSizeSelector={[100, 200, 500, 1000]}
            localeText={localeText}
            onGridReady={(params) => {
              setGridApi(params.api);
            }}
            onFirstDataRendered={OnFirstDataRendered}
            onRowSelected={(params) =>
              HandleMasterRowSelection(gridApi, params)
            }
            onRowDoubleClicked={HandleRowDoubleClicked}
            onCellClicked={(e) => {
              // 필요 시 특정 행 선택 등 처리
              const gridApi = props.gridRef1.current.api;
              const clickedRowIndex = e.rowIndex;

              const isCheckboxClick = e.event?.target?.type === 'checkbox';

              if (!isCheckboxClick) {
                gridApi.ensureIndexVisible(clickedRowIndex, 'middle');
                // gridApi.setFocusedCell(clickedRowIndex, 'coNm');

                const copied = JSON.parse(JSON.stringify(e.data));
                props.handleRowSelection1(copied, clickedRowIndex);

                gridApi
                  .getDisplayedRowAtIndex(clickedRowIndex)
                  ?.setSelected(true, false);
              }
            }}
            onSelectionChanged={(event) => {
              HandleMasterSelectionChanged(
                event,
                props.setSelectedRowData1,
                props.selectedRowNosRef1
              );
            }}
            onCellValueChanged={(event) =>
              HandleMasterCellValueChanged(
                event,
                props.setRowData1,
                props.setChangedRowData1
              )
            }
            style={{ height: '100%', width: '100%' }}
            className="ag-theme-alpine"
          />
        </Box>
      </Box>
      {/* 그리드안 사진 설정하기 팝업 창 */}
      {props.fileModalToggle && (
        <FileUploadDialog
          open={props.fileModalToggle}
          onClose={() => {
            props.setFileModalToggle(false);
          }}
          gridRef={props.gridRef1}
          selectedRowData={props.selectedRowData1}
          setSelectedRowData={props.setSelectedRowData1}
          setChangedRowData={props.setChangedRowData1}
          fileNameField="warningLabelFileName"
          saveNameField="warningLabelSaveName"
          filePathField="warningLabelFilePath"
          SaveLogicPreview={props.SaveLogicPreview}
          DownloadLogic={props.DownloadLogic1}
        />
      )}
      {/* 엑셀 업로드용 숨김 파일 input */}
      <input
        type="file"
        ref={props.fileInputRef}
        style={{ display: 'none' }}
        accept=".xlsx,.xls"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            props.ExcelUpload1?.(file);
            e.target.value = '';
          }
        }}
      />
    </Box>
  );
}

export default HAM_BASIC_02;
